import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ImageIcon, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiErrorDetail, resolveImageUrl } from "@/lib/api";

export const MediaPicker = ({ value, onChange, testid = "media-picker" }) => {
  const [open, setOpen] = useState(false);
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/admin/media").then((r) => setMedia(r.data)).catch(() => {});
    }
  }, [open]);

  const upload = async (fileList) => {
    if (!fileList?.length) return;
    setUploading(true);
    try {
      const data = new FormData();
      Array.from(fileList).forEach((f) => data.append("files", f));
      const res = await api.post("/admin/upload", data, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded to media library");
      onChange(res.data.urls[0]);
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <div className="flex gap-2">
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://... or /api/files/..."
          className="h-10 flex-1 rounded-sm border border-zinc-300 bg-white px-3 text-xs text-zinc-800 font-mono focus:outline-none focus:ring-1 focus:ring-[#FF6B00]"
          data-testid={`${testid}-input`}
        />
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="h-10 px-3 border border-zinc-300 text-zinc-600 hover:border-[#FF6B00] hover:text-[#FF6B00] rounded-sm text-xs font-bold uppercase tracking-wider shrink-0"
          data-testid={`${testid}-browse`}
        >
          <ImageIcon className="h-3.5 w-3.5 inline mr-1" /> Media
        </button>
      </div>
      {value && (
        <img src={resolveImageUrl(value)} alt="Selected" className="mt-2 h-16 w-auto border border-zinc-300 rounded-sm object-cover" data-testid={`${testid}-preview`} />
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-zinc-200 text-zinc-900 max-w-3xl max-h-[85vh] overflow-y-auto scrollbar-dark" data-testid="media-picker-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-zinc-900">Media Library</DialogTitle>
          </DialogHeader>
          <label className="w-full border-2 border-dashed border-zinc-300 hover:border-[#FF6B00] rounded-sm py-5 flex items-center justify-center gap-2 text-zinc-500 hover:text-[#FF6B00] cursor-pointer" data-testid="media-picker-upload">
            <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
            <span className="text-xs font-bold uppercase tracking-wider">{uploading ? "Uploading..." : "Upload New Image"}</span>
          </label>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-4">
            {media.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => { onChange(m.url); setOpen(false); }}
                className="relative aspect-video border border-zinc-300 hover:border-[#FF6B00] rounded-sm overflow-hidden"
                data-testid={`media-pick-${m.id}`}
              >
                <img src={resolveImageUrl(m.url)} alt={m.alt_text || m.original_filename} className="w-full h-full object-cover" loading="lazy" />
              </button>
            ))}
            {media.length === 0 && (
              <p className="col-span-full text-sm text-zinc-500 text-center py-6">No media yet. Upload your first image above.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
